import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {
  Box,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  disablePortal: true,
};

interface MultipleSelectProps {
  options: { label: string; value?: string; disabled?: boolean }[];
  option: string[];
  setOption: (value: string[]) => void;
  error?: boolean;
  placeholder?: string;
  testId?: string;
  id?: string;
  disabled?: boolean;
  onBlur?: () => void;
  onClose?: () => void;
}

export const MultipleSelect = ({
  options,
  option,
  setOption,
  error,
  placeholder,
  testId,
  id,
  disabled = false,
  onBlur,
  onClose,
}: MultipleSelectProps) => {
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const nextValue = typeof value === "string" ? value.split(",") : value;
    setOption(nextValue);
  };

  return (
    <Select
      labelId={id ? `${id}-label` : "multiple-checkbox-label"}
      id={id ?? "multiple-checkbox"}
      multiple
      value={option}
      data-testid={testId}
      fullWidth
      displayEmpty
      error={error}
      disabled={disabled}
      IconComponent={KeyboardArrowDownOutlinedIcon}
      onChange={handleChange}
      onBlur={onBlur}
      onClose={onClose}
      input={<OutlinedInput multiline error={error} />}
      renderValue={(selected) =>
        selected.length === 0 ? (
          <span style={{ color: theme.palette.text.placeholder }}>{placeholder}</span>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              gap: 0.5,
            }}
          >
            {selected.map((value) => {
              const selectedOption = options.find(
                (option) => (option.value ?? option.label) === value,
              );
              const optionDisabled = disabled || selectedOption?.disabled;
              return (
                <Chip
                  key={value}
                  disabled={optionDisabled}
                  label={selectedOption?.label ?? value}
                  deleteIcon={disabled ? undefined : <CloseIcon />}
                  onDelete={
                    optionDisabled
                      ? undefined
                      : () => {
                          const updated = option.filter((item) => item !== value);
                          setOption(updated);
                        }
                  }
                  onMouseDown={(event) => {
                    // prevent the Select from opening when clicking the chip / delete icon
                    event.stopPropagation();
                  }}
                />
              );
            })}
          </Box>
        )
      }
      MenuProps={MenuProps}
    >
      {options.map(({ label, value, disabled }) => {
        const optionValue = value ?? label;

        return (
          <MenuItem disabled={disabled} key={optionValue} value={optionValue}>
            <Checkbox checked={option.includes(optionValue)} />
            <ListItemText primary={label} />
          </MenuItem>
        );
      })}
    </Select>
  );
};
